import { useEffect, useState, type FormEvent } from "react";
import {
  normalizeUsername,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  validateUsername,
  type PublicUser,
  type PushNotificationPrefs,
} from "@fortress/shared";
import { getAppState } from "./appState";
import { fetchMe, savePushPrefs, setUsername } from "./auth/api";
import { logout, saveAccessSession, withAuthRetry } from "./auth/session";
import { sanitizeUsernameInput, usernameErrorMessage } from "./auth/username";
import { enableWebPush, webPushStatus, type WebPushStatus } from "./push";
import { SwitchToggle } from "./SwitchToggle";

// The hub's Account tab: the signed-in email, a rename form for the username
// (same character rules as the first-login picker, re-checked by the server),
// the account-wide push notification toggles, and Sign out.

const DEFAULT_PREFS: PushNotificationPrefs = {
  playerPings: true,
  matchPings: true,
};

/** The session is gone — back through the login gate, returning here. */
function toAuth(): void {
  window.location.replace(`/auth?returnTo=${encodeURIComponent("/account")}`);
}

export function AccountPanel({ authenticated }: { authenticated: boolean }) {
  if (!authenticated) {
    return (
      <section className="lobby-hub-panel account-panel">
        <h2>Account</h2>
        <p className="lobby-hub-empty">
          You&apos;re playing as a guest — sign-in is disabled on this server.
        </p>
      </section>
    );
  }
  return <SignedInAccount />;
}

function SignedInAccount() {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [username, setUsernameInput] = useState("");
  const [usernameBusy, setUsernameBusy] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [usernameSaved, setUsernameSaved] = useState(false);

  /** Push opt-outs live on the account (`User.pushNotifications`) and gate
   * the server's FCM sends across ALL the account's devices; missing prefs
   * mean everything enabled. Saved optimistically, reverted on failure. */
  const [prefs, setPrefs] = useState<PushNotificationPrefs | null>(null);
  // This browser's own push standing (web only — native handles permission
  // at boot; `unsupported` also covers missing Firebase web config, no row).
  const [webStatus, setWebStatus] = useState<WebPushStatus>(webPushStatus);

  const [signingOut, setSigningOut] = useState(false);
  /** The replay link shows only while the server offers the tutorial. */
  const [tutorialEnabled, setTutorialEnabled] = useState(false);
  useEffect(() => {
    getAppState()
      .then((s) => setTutorialEnabled(s.tutorialEnabled === true))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let stale = false;
    withAuthRetry((token) => fetchMe(token))
      .then((me) => {
        if (stale) return;
        if (!me) {
          toAuth(); // the account no longer exists
          return;
        }
        setUser(me);
        setUsernameInput(me.username ?? "");
        setPrefs(me.pushNotifications ?? DEFAULT_PREFS);
      })
      .catch((err: unknown) => {
        if (stale) return;
        if (err instanceof Error && err.message === "not_authenticated") {
          toAuth();
          return;
        }
        setLoadError("Couldn't load your account. Try again.");
      });
    return () => {
      stale = true;
    };
  }, []);

  async function onSaveUsername(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeUsername(username);
    const validation = validateUsername(normalized);
    setUsernameSaved(false);
    if (validation) {
      setUsernameError(usernameErrorMessage(validation));
      return;
    }
    setUsernameBusy(true);
    setUsernameError(null);
    try {
      const session = await withAuthRetry((token) =>
        setUsername(normalized, token),
      );
      await saveAccessSession(session);
      setUser(session.user);
      setUsernameInput(session.user.username ?? normalized);
      setUsernameSaved(true);
    } catch (err) {
      if (err instanceof Error && err.message === "not_authenticated") {
        toAuth();
        return;
      }
      setUsernameError(
        usernameErrorMessage(err instanceof Error ? err.message : ""),
      );
    } finally {
      setUsernameBusy(false);
    }
  }

  const setPref = (key: keyof PushNotificationPrefs, on: boolean) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: on };
    setPrefs(next);
    void withAuthRetry((token) => savePushPrefs(next, token)).catch(() => {
      setPrefs(prefs); // revert — the server never saw it
    });
  };

  async function signOut() {
    setSigningOut(true);
    await logout();
    window.location.replace("/auth");
  }

  const unchanged = normalizeUsername(username) === (user?.username ?? "");

  return (
    <div className="account-panel">
      <section className="lobby-hub-panel">
        <h2>Profile</h2>
        {loadError && <p className="auth-error">{loadError}</p>}
        <dl className="account-facts">
          <dt>Email</dt>
          <dd>{user ? (user.email ?? "No email on this account") : "…"}</dd>
          <dt>Username</dt>
          <dd>{user ? (user.username ?? "Not set") : "…"}</dd>
        </dl>
        <form className="lobby-hub-form" onSubmit={onSaveUsername}>
          <label className="auth-label" htmlFor="account-username">
            Change username
          </label>
          <div className="account-username-row">
            <input
              id="account-username"
              className="auth-input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="e.g. fortress.ace"
              value={username}
              onChange={(e) => {
                setUsernameInput(sanitizeUsernameInput(e.target.value));
                setUsernameSaved(false);
              }}
              maxLength={USERNAME_MAX_LENGTH}
              disabled={!user || usernameBusy}
            />
            <button
              className="auth-submit"
              type="submit"
              disabled={!user || usernameBusy || unchanged}
            >
              {usernameBusy ? "Saving…" : "Save"}
            </button>
          </div>
          <p className="account-hint">
            {USERNAME_MIN_LENGTH}–{USERNAME_MAX_LENGTH} characters: lowercase
            letters and numbers, with . or - between them.
          </p>
          {usernameError && <p className="auth-error">{usernameError}</p>}
          {usernameSaved && <p className="account-ok">Username updated.</p>}
        </form>
      </section>

      <section className="lobby-hub-panel">
        <h2>Notifications</h2>
        <div className="account-prefs">
          <SwitchToggle
            className="account-pref"
            checked={prefs?.matchPings ?? true}
            disabled={!prefs}
            onChange={(on) => setPref("matchPings", on)}
          >
            Match alerts — round starts &amp; your turn
          </SwitchToggle>
          <SwitchToggle
            className="account-pref"
            checked={prefs?.playerPings ?? true}
            disabled={!prefs}
            onChange={(on) => setPref("playerPings", on)}
          >
            Player pings — honks aimed at you
          </SwitchToggle>
          {webStatus === "prompt" && (
            <button
              type="button"
              className="push-settings-enable"
              onClick={() => void enableWebPush().then(setWebStatus)}
            >
              Enable on this browser
            </button>
          )}
          {webStatus === "granted" && (
            <div className="push-settings-note">✓ Enabled on this browser</div>
          )}
          {webStatus === "denied" && (
            <div className="push-settings-note">
              Blocked — allow notifications in your browser&apos;s site settings
            </div>
          )}
        </div>
      </section>

      <button
        type="button"
        className="sign-out-button"
        onClick={() => void signOut()}
        disabled={signingOut}
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
      {tutorialEnabled && (
        <a className="auth-link" href="/tutorial">
          Replay the tutorial
        </a>
      )}
    </div>
  );
}
